import { Fragment, useRef, useState } from 'react';
import { Order, OrderMedia, OrderMediaType } from '../../../clients/quipli-service/order/order';
import { Collapsible, CollapsibleRef } from '../../../components/collapsible/collapsible';
import { toast } from '../../../util/toasts';
import { OrderSection } from '../order-section/order-section';
import { Typography } from '../../../components/typography/typography';
import { Spinner } from '../../../components/spinner/spinner';
import { FileField } from '../../../components/field/field';
import classNames from 'classnames';
import Icon from '../../../components/icons/icon';
import { ConfirmModal } from '../../../components/modal/confirm-modal';
import { useOrderApiV1 } from '../../../clients/quipli-service/order/order-api-v1';
import { ImageSize } from '../../../clients/quipli-service/image/image';
import { useOrderMedia } from '../hooks/use-order-media';
import { noop } from 'lodash';
import { useCompatibleDownload } from '../../../hooks/use-compatible-download';

export const OrderMediaList = ({ order }: { order: Order }) => {
  const { isOrderMediaLoading, mediaList, fetch, upload, isUploading } = useOrderMedia(order);

  const ref = useRef<CollapsibleRef>(null);

  return (
    <>
      <OrderSection>
        <Collapsible
          openAtStart
          ref={ref}
          trigger={(isOpen) => (
            <div className="flex justify-between items-center">
              <Typography type="text-md" weight="medium" className="text-grey-800">
                Media
              </Typography>

              <Icon icon={isOpen ? 'ChevronUp' : 'ChevronDown'} size="small" />
            </div>
          )}
          afterHiddenContent={
            isUploading ? (
              <Spinner />
            ) : (
              <FileField
                multiple
                className="mt-[8px]"
                accept="image/jpeg, image/png, video/mp4, video/quicktime, application/pdf"
                value={undefined}
                onChange={(files) => upload(files as File[], '', async () => noop())}
              />
            )
          }
        >
          <div
            className={classNames('flex overflow-x-auto gap-[8px] w-full', {
              'justify-center': isOrderMediaLoading,
              'mt-[8px] py-[4px]': mediaList.length > 0,
            })}
          >
            {isOrderMediaLoading ? (
              <Spinner />
            ) : (
              mediaList.map((media, idx) => (
                <Fragment key={`order-media-${idx}`}>
                  <MediaCard media={media} reload={fetch} />
                </Fragment>
              ))
            )}
          </div>
        </Collapsible>
      </OrderSection>
    </>
  );
};

export const MediaCard = ({
  media,
  reload,
  enableControls = true,
}: {
  media: OrderMedia;
  reload: () => void;
  enableControls?: boolean;
}) => {
  const { deleteOrderMedia, getDownloadOrderMediaUrl } = useOrderApiV1();
  const { download } = useCompatibleDownload();

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const onConfirm = async () => {
    if (isDeleting) return;
    setIsDeleting(true);

    try {
      await deleteOrderMedia(media.orderId, media.id);

      toast('Order media deleted successfully.');
      setIsOpen(false);
      reload();
    } catch (error: any) {
      toast(error.message.msg || error.message || 'Something went wrong.', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const onOpen = async (forceDownload: boolean) => {
    try {
      await download(() => getDownloadOrderMediaUrl(media.orderId, media.id, forceDownload, ImageSize.Original), {
        newTab: true,
        forceDownload,
        filename: media.filename,
      });
    } catch (error: any) {
      toast(error.message.msg || error.message || 'Something went wrong.', 'error');
    }
  };

  return (
    <>
      <div className="shrink-0 flex flex-col items-center gap-[6px]">
        <div className="w-[140px] h-[140px] rounded-[8px] border border-solid border-grey-200 relative flex items-center justify-center">
          {enableControls && (
            <div className="absolute w-fit right-0 top-0 p-[4px] flex gap-[6px]">
              {/* View */}
              {media.type !== OrderMediaType.Document && (
                <div
                  className="cursor-pointer [&>*]:stroke-grey-400 hover:[&>*]:stroke-grey-700"
                  onClick={() => onOpen(false)}
                >
                  <Icon icon="LinkExternal" />
                </div>
              )}

              {/* Download */}
              <div
                className="cursor-pointer [&>*]:stroke-grey-400 hover:[&>*]:stroke-grey-700"
                onClick={() => onOpen(true)}
              >
                <Icon icon="Download" />
              </div>

              {/* Delete */}
              <div
                className="cursor-pointer [&>*]:stroke-grey-400 hover:[&>*]:stroke-grey-700"
                onClick={() => setIsOpen(true)}
              >
                <Icon icon="Trash" />
              </div>
            </div>
          )}

          <div className="mt-[35px]">
            {media.type === 'Image' ? (
              <div className="cursor-pointer" onClick={() => onOpen(false)}>
                <img className="h-[100px] w-[100px] object-contain" src={media.downloadUrl} alt={media.filename} />
              </div>
            ) : media.type === 'Video' ? (
              <video className="h-[100px] w-[100px]" controls muted>
                <source src={media.downloadUrl} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            ) : (
              <div className="h-[100px] w-[100px]">
                <div className="justify-center items-center cursor-pointer" onClick={() => onOpen(false)}>
                  <div className="flex justify-center items-center">
                    <Icon icon="File" size="xxlarge" />
                  </div>
                  <div className="flex justify-center items-center">
                    <Typography type="text-xs" className="text-grey-400">
                      {media.filename}
                    </Typography>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <Typography type="text-xs" className="text-grey-400">
          {media.uploadedAt.toFormat('ff')}
        </Typography>
      </div>

      <ConfirmModal
        isOpen={isOpen}
        prompt="Delete File"
        headerIcon={
          <div className="flex justify-center">
            <Icon icon="InfoCircle" size="xlarge" stroke="var(--error-500)" />
          </div>
        }
        additionalText={
          <div className="text-center px-4">
            <Typography type="text-sm" className="text-grey-500">
              Are you sure you want to delete this file?
              <br />
              {`${media.filename} will be permanently deleted.`}
            </Typography>
          </div>
        }
        yesButtonYellow
        yesLabel="Delete"
        noLabel="Cancel"
        onConfirm={() => onConfirm()}
        close={() => setIsOpen(false)}
      />
    </>
  );
};
